<template>
  <div class="fifty-banner critical-component-clear">
    <splide
      v-if="
        blocks.length === 2 &&
          (blocks[0].desktopImage || blocks[0].desktopVideo) &&
          (blocks[1].desktopImage || blocks[1].desktopVideo)
      "
      class="fifty-banner__carousel"
      :class="{ 'mobile-slide': settings.isMobileSlide }"
      :has-track="false"
      :options="localConfig"
    >
      <splide-track>
        <splide-slide
          v-for="(media, index) of blocks"
          :key="index"
        >
          <div
            class="fifty-banner__content"
            :class="[
              media.textColor,
              {
                'fifty-banner__content--second': index,
                [settings.heightMobile]: !isMobile,
                [settings.heightDesktop]: !isMobile,
                ['mobile-' + settings.heightMobile]: true,
              }
            ]"
            :style="media.spacing"
          >
            <template
              v-if="
                (isMobile && !media.mobileVideo) ||
                  (!isMobile && !media.desktopVideo)
              "
            >
              <div class="fifty-banner__image-wrapper">
                <a
                  v-if="media.bannerLink"
                  class="fifty-banner__link"
                  :href="$localizeUrl(media.bannerLink)"
                  v-text="media.title"
                />

                <responsive-image
                  v-if="media.mobileImage || media.desktopImage"
                  class="fifty-banner__image mobile"
                  :image="getImage(media.mobileImage || media.desktopImage)"
                  :loaded-animation="false"
                  :padding="false"
                  ratio="1:1"
                  :sizes="`(min-width: ${breakpointL}px) 50vw, 100vw`"
                />

                <responsive-image
                  v-if="media.tabletImage"
                  class="fifty-banner__image tablet"
                  :image="getImage(media.tabletImage)"
                  :loaded-animation="false"
                  :padding="false"
                  ratio="1:1"
                  :sizes="`(min-width: ${breakpointL}px) 50vw, 100vw`"
                />

                <responsive-image
                  v-if="media.desktopImage"
                  class="fifty-banner__image desktop"
                  :image="getImage(media.desktopImage)"
                  :loaded-animation="false"
                  :padding="false"
                  ratio="1:1"
                  :sizes="`(min-width: ${breakpointL}px) 50vw, 100vw`"
                />
              </div>

              <div
                v-if="!media.atcProductId"
                :class="media.contentPosition"
                class="fifty-banner__link-wrapper"
              >
                <div
                  class="fifty-banner__text-button-wrapper"
                >
                  <component
                    :is="bannerTitleComponent(media)"
                    v-if="!media.hotspotProducts?.length && media.title"
                    class="fifty-banner__title"
                    :class="
                      `${media.titleFontDesktop}
                      ${media.titleFontMobile}
                      ${media.titleFontWeightDesktop}
                      ${media.titleFontWeightMobile}
                      ${media.titleFontFamily}`"
                    :href="$localizeUrl(media.bannerLink)"
                    v-text="media.title"
                  />

                  <btn
                    v-if="!media.hotspotProducts?.length && media.ctaTitle && media.ctaUrl"
                    class="fifty-banner__button transparent"
                    :label="media.ctaTitle"
                    type="button"
                    :url="media.bannerLink ? '' : $localizeUrl(media.ctaUrl)"
                    :modifiers="getButtonModifiers(media.ctaType, media.ctaColor)"
                  />

                  <div
                    v-if="media.hotspotProducts?.length"
                    class="fifty-banner__hotspot-cta-wrapper"
                  >
                    <button
                      class="
                        fifty-banner__hotspot-cta
                        shoppable-hotspot-cta
                      "
                      type="button"
                      @click.prevent="showHotspot(index === 0 ? 'left' : 'right')"
                    >
                      <icon-cart />

                      <span
                        class="
                          shoppable-hotspot-cta__label
                          text-body-2xs-regular
                        "
                        v-text="$string('lookbook.shop_the_look')"
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="fifty-banner__atc-wrapper"
              >
                <button
                  class="fifty-banner__atc"
                  :label="addToCartText"
                  type="submit"
                  show-icon
                  @click="addItemToCart(media.atcProductId)"
                >
                  <icon-cart />
                </button>

                <div
                  v-if="media.hotspotProducts?.length"
                  class="fifty-banner__hotspot-cta-wrapper"
                >
                  <button
                    class="
                      fifty-banner__hotspot-cta
                      shoppable-hotspot-cta
                    "
                    type="button"
                    @click.prevent="showHotspot(index === 0 ? 'left' : 'right')"
                  >
                    <icon-cart />

                    <span
                      class="
                        shoppable-hotspot-cta__label
                        text-body-2xs-regular
                      "
                      v-text="$string('lookbook.shop_the_look')"
                    />
                  </button>
                </div>
              </div>
            </template>

            <template v-else>
              <div
                class="
                  fifty-banner__image-wrapper
                  fifty-banner__video-wrapper
                "
              >
                <a
                  v-if="media.bannerLink"
                  class="fifty-banner__link fifty-banner__link--video"
                  :href="$localizeUrl(media.bannerLink)"
                  v-text="media.title"
                />

                <video-player
                  v-if="(!isMobile && media.desktopVideo) || (isMobile && !media.mobileVideo)"
                  :id="media.desktopVideo.id"
                  class="fifty-banner__video desktop"
                  :controls="{
                    hide: true,
                    id: `${liquid.sectionId}-${index}-desktop`,
                    muteButton: media.videoAudioControls,
                  }"
                  :intersection-observer="{
                    enable: media.videoAutoPlay,
                  }"
                  :is-dark="media.textColor === 'dark'"
                  :image="media.desktopVideo.preview_image"
                  :sources="media.desktopVideo.sources"
                  :poster="media.videoPoster"
                />

                <video-player
                  v-else-if="isMobile && media.mobileVideo"
                  :id="media.mobileVideo.id"
                  class="fifty-banner__video mobile"
                  :controls="{
                    id: `${liquid.sectionId}-${index}-mobile`,
                    muteButton: media.videoAudioControls,
                  }"
                  :intersection-observer="{
                    enable: media.videoAutoPlay,
                  }"
                  :is-dark="media.textColor === 'dark'"
                  :image="media.mobileVideo.preview_image"
                  :sources="media.mobileVideo.sources"
                  :poster="media.mobileVideoPoster"
                  :lazyload="false"
                  preload
                />
              </div>

              <div
                v-if="media.title || media.hotspotProducts?.length"
                class="fifty-banner__video-title-wrapper"
                :class="media.contentPosition"
              >
                <component
                  :is="bannerTitleComponent(media)"
                  class="fifty-banner__title"
                  :class="
                    `${media.titleFontDesktop}
                    ${media.titleFontMobile}
                    ${media.titleFontWeightDesktop}
                    ${media.titleFontWeightMobile}
                    ${media.titleFontFamily}`"
                  :href="$localizeUrl(media.bannerLink)"
                  v-text="media.title"
                />

                <div
                  v-if="media.hotspotProducts?.length"
                  class="fifty-banner__hotspot-cta-wrapper"
                >
                  <button
                    class="
                        fifty-banner__hotspot-cta
                        shoppable-hotspot-cta
                      "
                    type="button"
                    @click.prevent="showHotspot(index === 0 ? 'left' : 'right')"
                  >
                    <icon-cart />

                    <span
                      class="
                          shoppable-hotspot-cta__label
                          text-body-2xs-regular
                        "
                      v-text="$string('lookbook.shop_the_look')"
                    />
                  </button>
                </div>
              </div>
            </template>

            <div
              v-if="media.hotspotProducts"
              class="fifty-banner__hotspot-wrapper"
            >
              <shoppable-hotspot
                :products="media.hotspotProducts"
                :product-card-location="{
                  key: 'sectionHotspot',
                  metaobjectName: ''
                }"
                show-backdrop
                :active="Object.values(hotspotShowing)?.[index]"
                @closed="hideHotspot(index === 0 ? 'left' : 'right')"
              />
            </div>
          </div>
        </splide-slide>
      </splide-track>

      <div
        class="fifty-banner__arrows splide__arrows"
      >
        <button
          class="splide__arrow splide__arrow--prev"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button
          class="splide__arrow splide__arrow--next"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>
    </splide>
  </div>
</template>

<script>

/**
 * Vue: fifty-banner (fifty-banner)
 * -----------------------------------------------------------------------------
 * 2 Blocks (50/50 Banner) Section.
 *
 * @param {Array} blocks - Section blocks.
 * @param {Object} liquid - Liquid settings.
 * @param {Object} settings - Section settings.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayer from '~async/video-player/video-player'

import IconCart from '~icons/general/cart.svg'
import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'
import ShoppableHotspot from '~async/shoppable-hotspot/shoppable-hotspot'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'FiftyBanner',

  components: {
    Btn,
    IconArrowLeft,
    IconArrowRight,
    IconCart,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer,
    ShoppableHotspot,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      breakpointL: breakpoints.l,
      localConfig: {},
      state: 'ready',
      hotspotShowing: {
        left: false,
        right: false,
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      return this.$string('product.cta.add_to_cart')
    },
  },

  beforeMount() {
    this.localConfig = {
      arrows: false,
      direction: 'ltr',
      drag: false,
      pagination: false,
      perPage: 2,
      speed: this.$timing('normal'),
      start: 0,
      type: 'slide',
      breakpoints: {
        [breakpoints.l]: {
          arrows: this.settings.isMobileSlide,
          drag: this.settings.isMobileSlide,
          perPage: 1,
        },
      },
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
    }),

    /**
     * Add to cart handler
     */
    async addItemToCart(itemID) {
      clearTimeout(this.timeout)
      this.state = 'loading'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('slow'))

        return
      }

      const items = [
        {
          id: itemID,
          quantity: 1,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'ready'

        if (cnvs.settings.autoOpenCartDrawer) {
          window.cnvs?.EventBus?.emit('overlay:open-cart-drawer')
        }

      } catch (error) {
        this.state = 'ready'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Get button modifiers
     * @param {String} type - Button type
     * @param {String} color - Button color
     */
    getButtonModifiers(type, color) {
      if (color === 'light') {
        return `${type} light`
      }

      return `${type} dark`
    },

    /**
     * Get image
     */
    getImage(image) {
      if (image.src) {
        return image
      }
      return {
        src: image,
      }
    },

    /**
     * Wraps banner title per link provided
     */
    bannerTitleComponent(bannerData) {
      if (bannerData.bannerLink) {
        return 'a'
      }

      return 'p'
    },

    /**
     * Show hotspot
     * @param side
     */
    showHotspot(side) {
      if (side === 'left') {
        this.hotspotShowing.right = false
        this.hotspotShowing.left = true

        return
      }

      this.hotspotShowing.left = false
      this.hotspotShowing.right = true
    },

    /**
     * Hide hotspot
     * @param side
     */
    hideHotspot(side) {
      if (side === 'left') {
        this.hotspotShowing.left = false

        return
      }

      this.hotspotShowing.right = false
    },
  },
}
</script>

<style lang="scss">
@import './fifty-banner';
</style>
